import React from 'react'
import {
  Avatar, Box, Button, Dialog, DialogActions, IconButton, Stack, ThemeProvider, Typography, createTheme, useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Subscriber } from '../../store/user/reducer'
import { formatDate } from '../../function/date'
import { brand, NETWORK, Tokens } from '../../container/result-v2/tokens'
import { FieldTable, NoData, RawCard, ResCard, useTokens } from '../../container/result-v2/ui/primitives'

/*
 * AI assistant tab of subscriber_v2 — the subscriber's IRBI conversation
 * exactly as the user sees it in the new UI (espy-dashboard
 * searcher-v2/parts/AskIrbiDock.tsx + AskIrbiAssistant.tsx): the IRBI header,
 * the same bubbles, and the same rendering of an answer — a lookup reply
 * (combined_phone / combined_email / combined_name) as one row per source
 * with a Details dialog, the assistant's warning and "still running" cards,
 * and "Show results" for any other structured reply. Ported, not imported:
 * the two apps do not share code.
 *
 * Read only — the classic tab's data, `subscriber.chatHistory`.
 */

interface Chat {
  role: string
  text: string
  type?: string
  link?: string
  dateTime?: string
}

const RICH_TYPES = new Set(['combined_phone', 'combined_email', 'combined_name'])

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}
function isNonEmptyObject(v: unknown): v is Record<string, unknown> {
  return isPlainObject(v) && Object.keys(v).length > 0
}

function toAvatarSrc(v?: string | string[] | null): string | undefined {
  const raw = Array.isArray(v) ? v.find(Boolean) : v
  if (!raw) return undefined
  const s = String(raw).trim()
  if (/^(https?:\/\/|data:image\/|blob:)/i.test(s)) return s
  return `data:image/png;base64,${s.replace(/^data:.*;base64,/, '').replace(/\s+/g, '')}`
}

function first(v: unknown): string | undefined {
  if (Array.isArray(v)) return v.find(Boolean) ? String(v.find(Boolean)) : undefined
  return v ? String(v) : undefined
}

function initialsFrom(v?: string): string {
  const trimmed = (v || '').trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/).filter(Boolean)
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : trimmed.slice(0, 2)).toUpperCase()
}

/* the user UI badges a row with the network's sprite; the admin has no such sprite, so its colour and initial */
function sourceBadge(source?: string): React.ReactNode {
  if (!source) return null
  const meta = NETWORK[String(source).trim().toUpperCase()]
  if (!meta) return null
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: meta.color, color: '#fff', fontSize: 8, fontWeight: 800 }}>
      {meta.label.charAt(0)}
    </Box>
  )
}

function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : key
}

function formatFieldValue(v: unknown): React.ReactNode | null {
  if (v == null) return null
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (Array.isArray(v)) {
    const parts = v.filter((x) => x != null && x !== '' && typeof x !== 'object').map(String)
    return parts.length ? parts.join(', ') : null
  }
  if (typeof v === 'object') return null
  const s = String(v).trim()
  return s || null
}

/* every non-empty field, plus one level of nested objects under a prefix */
function buildFieldRows(raw: unknown, prefix = '', depth = 0): [string, React.ReactNode][] {
  if (!isPlainObject(raw)) return []
  const rows: [string, React.ReactNode][] = []
  for (const [key, value] of Object.entries(raw)) {
    if (value == null || value === '') continue
    const label = prefix ? `${prefix} ${humanizeKey(key)}` : humanizeKey(key)
    if (isPlainObject(value)) {
      if (depth < 1) rows.push(...buildFieldRows(value, label, depth + 1))
      continue
    }
    const formatted = formatFieldValue(value)
    if (formatted != null) rows.push([label, formatted])
  }
  return rows
}

type RowData = {
  key: string
  avatarUrl?: string
  initials: string
  badge: React.ReactNode
  primary: React.ReactNode
  secondary?: string
  raw: any
}

function DetailsDialog({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) {
  const tk = useTokens()
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { background: tk.surface, color: tk.text, borderRadius: '18px', p: '20px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', backgroundImage: 'none' } }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 34, height: 34, borderRadius: '11px', display: 'grid', placeItems: 'center', color: brand.primary, background: tk.brand050, flex: 'none' }}>
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: tk.text }}>Details</Typography>
          <Typography sx={{ fontSize: 12, color: tk.text3 }}>raw response included below</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="close" sx={{ color: tk.text3 }}><CloseIcon fontSize="small" /></IconButton>
      </Stack>
      <Box sx={{ mt: '16px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto', minHeight: 0 }}>
        {children}
      </Box>
      <DialogActions sx={{ px: 0, pb: 0, pt: '16px' }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function ResultRow({ row, tk }: { row: RowData; tk: Tokens }) {
  const [open, setOpen] = React.useState(false)
  const fieldRows = React.useMemo(() => buildFieldRows(row.raw), [row.raw])

  return (
    <>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: '8px', borderTop: `1px solid ${tk.border}` }}>
        <Box sx={{ position: 'relative', flex: 'none' }}>
          <Avatar src={row.avatarUrl} sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: tk.brand050, color: brand.primary }}>
            {row.initials}
          </Avatar>
          {row.badge && (
            <Box sx={{
              position: 'absolute', bottom: -2, right: -2, width: 14, height: 14, borderRadius: '50%',
              display: 'grid', placeItems: 'center', background: tk.surface2, border: `1px solid ${tk.surface3}`, overflow: 'hidden',
            }}>
              {row.badge}
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.primary}
          </Typography>
          {row.secondary && (
            <Typography sx={{ display: 'block', fontSize: 12, color: tk.text3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.secondary}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={() => setOpen(true)} aria-label="View raw data" sx={{ color: tk.text3, flex: 'none' }}>
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      <DetailsDialog open={open} onClose={() => setOpen(false)}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar src={row.avatarUrl} sx={{ width: 40, height: 40, fontSize: 14, fontWeight: 700, bgcolor: tk.brand050, color: brand.primary }}>
            {row.initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: tk.text }}>{row.primary}</Typography>
            {row.secondary && <Typography sx={{ display: 'block', fontSize: 12.5, color: tk.text3 }}>{row.secondary}</Typography>}
          </Box>
        </Stack>
        {fieldRows.length > 0 ? (
          <ResCard title="Details"><FieldTable rows={fieldRows} /></ResCard>
        ) : (
          <NoData title="No structured fields" hint="See the raw response below." />
        )}
        <RawCard data={row.raw} />
      </DetailsDialog>
    </>
  )
}

/* a payload of source-keyed objects in data[] (PLUTO, tc, getcontact, …) — one block per source */
function extractSourceEntries(data: unknown): { source: string; fields: [string, React.ReactNode][] }[] {
  if (!isPlainObject(data) || !Array.isArray((data as any).data)) return []
  const entries: { source: string; fields: [string, React.ReactNode][] }[] = []
  for (const item of (data as any).data) {
    if (!isPlainObject(item)) continue
    for (const [source, v] of Object.entries(item)) {
      if (isNonEmptyObject(v)) entries.push({ source, fields: buildFieldRows(v) })
    }
  }
  return entries
}

function RawResultButton({ data, tk }: { data: unknown; tk: Tokens }) {
  const [open, setOpen] = React.useState(false)
  const sourceEntries = React.useMemo(() => extractSourceEntries(data), [data])
  const fieldRows = React.useMemo(() => buildFieldRows(data), [data])

  return (
    <Box>
      <Typography sx={{ display: 'block', fontSize: 13, lineHeight: 1.5, mb: '8px' }}>Got a result for this — open it below.</Typography>
      <Button
        onClick={() => setOpen(true)}
        variant="outlined"
        size="small"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: 15 }} />}
        sx={{ borderRadius: '999px', textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}
      >
        Show results
      </Button>
      <DetailsDialog open={open} onClose={() => setOpen(false)}>
        {sourceEntries.length > 0 ? (
          sourceEntries.map((e, i) => (
            <ResCard key={i} title={humanizeKey(e.source)}>
              {e.fields.length > 0 ? <FieldTable rows={e.fields} /> : <Typography sx={{ fontSize: 12.5, color: tk.text3 }}>No fields</Typography>}
            </ResCard>
          ))
        ) : fieldRows.length > 0 ? (
          <ResCard title="Details"><FieldTable rows={fieldRows} /></ResCard>
        ) : (
          <NoData title="No structured fields" hint="See the raw response below." />
        )}
        <RawCard data={data} />
      </DetailsDialog>
    </Box>
  )
}

function ResultGroup({ label, rows, tk }: { label: string; rows: RowData[]; tk: Tokens }) {
  if (rows.length === 0) return null
  return (
    <Box>
      <Typography sx={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: tk.text3, mb: '2px' }}>
        {label} · {rows.length} source{rows.length === 1 ? '' : 's'}
      </Typography>
      {rows.map((row) => <ResultRow key={row.key} row={row} tk={tk} />)}
    </Box>
  )
}

function personRowData(el: any, i: number): RowData {
  const linkedInName = el?.person?.firstName && el?.person?.lastName ? `${el.person.firstName} ${el.person.lastName}` : null
  const name = linkedInName || first(el?.name) || first(el?.contact?.name) || (Array.isArray(el?.names) && el.names[0]?.name)
  const email = first(el?.email)
  const identifier = el?.person?.publicIdentifier
  const city = el?.company?.headquarter?.city || first(el?.city)
  const country = el?.company?.headquarter?.country || first(el?.country)
  const avatar = el?.person?.photoUrl || el?.avatar
  const primaryText = name || email || identifier || el?.source || 'Result'
  const secondary = (name && email ? email : null)
    || (identifier && identifier !== primaryText ? `@${identifier}` : null)
    || [city, country].filter(Boolean).join(', ') || undefined
  return {
    key: `${el?.source ?? 'row'}-${i}`,
    avatarUrl: toAvatarSrc(avatar),
    initials: initialsFrom(name || email || el?.source),
    badge: sourceBadge(el?.source),
    primary: primaryText,
    secondary,
    raw: el,
  }
}

function nameRowData(item: any, i: number): RowData {
  const primaryText = item?.profileName || item?.profile_id || item?.source || 'Result'
  const handle = item?.profile_id && item.profile_id !== primaryText ? `@${item.profile_id}` : null
  const stats = [
    item?.following_count != null ? `${item.following_count} following` : null,
    item?.followers_count != null ? `${item.followers_count} followers` : null,
    item?.posts_count != null ? `${item.posts_count} posts` : null,
  ].filter(Boolean).join(' · ')
  return {
    key: String(item?.profile_id ?? item?.source ?? i),
    avatarUrl: toAvatarSrc(item?.profileImageUrl),
    initials: initialsFrom(item?.profileName || item?.profile_id),
    badge: sourceBadge(item?.social_network),
    primary: handle ? <>{primaryText} <Box component="span" sx={{ fontWeight: 400, opacity: 0.65 }}>{handle}</Box></> : primaryText,
    secondary: stats || item?.location || undefined,
    raw: item,
  }
}

function renderErrorCard(message: string, tk: Tokens): React.ReactNode {
  const [main, exampleRaw] = message.split(/\s*Example:\s*/i)
  const example = exampleRaw ? exampleRaw.trim().replace(/^["“]|["”]\.?$/g, '') : null
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
        <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: brand.warn }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: brand.warn }}>Warning</Typography>
      </Stack>
      <Typography sx={{ display: 'block', fontSize: 13, lineHeight: 1.5, mb: example ? 1 : 0 }}>{main.trim()}</Typography>
      {example && (
        <Box sx={{ background: tk.surface2, borderRadius: '8px', px: '10px', py: '6px' }}>
          <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{example}</Box>
        </Box>
      )}
    </Box>
  )
}

function renderProgressCard(message: string, tk: Tokens): React.ReactNode {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
        <AccessTimeRoundedIcon sx={{ fontSize: 16, color: brand.warn }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: brand.warn }}>Still running</Typography>
      </Stack>
      <Typography sx={{ display: 'block', fontSize: 13, lineHeight: 1.5, color: tk.text }}>{message.replace(/^\W+\s*/, '')}</Typography>
    </Box>
  )
}

/* a lookup answer arrives as JSON in `text` — drawn as the user's panel draws it */
function renderRichBody(chat: Chat, tk: Tokens): React.ReactNode {
  const parsed = (() => {
    if (typeof chat.text !== 'string') return null
    try {
      const v = JSON.parse(chat.text)
      return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
    } catch {
      return null
    }
  })()
  if (!parsed) return chat.text
  if (typeof parsed.error === 'string') return renderErrorCard(parsed.error, tk)
  if (parsed.success === true && parsed.status === 'progress') {
    const message = (parsed.data as any)?.message
    if (typeof message === 'string') return renderProgressCard(message, tk)
  }
  const type = (parsed.type as string) || null
  if (!type || !RICH_TYPES.has(type)) return <RawResultButton data={parsed} tk={tk} />

  if (type === 'combined_phone' || type === 'combined_email') {
    const dataArr: any[] = Array.isArray((parsed as any).data) ? (parsed as any).data : []
    const result = dataArr.flatMap((item: any) => Object.entries(item)
      .filter(([, v]) => isNonEmptyObject(v))
      .map(([source, v]) => ({ source, ...(v as any) })))
    return (
      <ResultGroup
        label={type === 'combined_phone' ? 'Phone lookup results' : 'E-mail lookup results'}
        rows={result.map((el, i) => personRowData(el, i))}
        tk={tk}
      />
    )
  }
  const namesData: any[] = (parsed as any)?.data?.[0]?.allservicesnames ?? (parsed as any)?.data?.[0]?.allservicenames ?? []
  return <ResultGroup label="Name lookup results" rows={namesData.map((item, i) => nameRowData(item, i))} tk={tk} />
}

function Thread({ chats }: { chats: Chat[] }) {
  const tk = useTokens()
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // open on the latest messages, as the user's panel does — the thread scrolls, never the page
  React.useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [chats])

  return (
    <Box sx={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: '14px', overflow: 'hidden', color: tk.text }}>
      <Box sx={{ p: '16px 18px', borderBottom: `1px solid ${tk.border}`, display: 'flex', alignItems: 'center', gap: '11px' }}>
        <Box sx={{ width: 34, height: 34, borderRadius: '11px', flex: 'none', display: 'grid', placeItems: 'center', color: '#fff', background: 'linear-gradient(140deg,#2D89F4,#7C5CFF)' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="4" y="7" width="16" height="12" rx="4" />
            <circle cx="9" cy="13" r="1.3" fill="currentColor" />
            <circle cx="15" cy="13" r="1.3" fill="currentColor" />
            <path d="M12 7V4" />
          </svg>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ display: 'block', fontWeight: 800, fontSize: 14, color: tk.text }}>IRBI</Typography>
          <Typography sx={{ display: 'block', fontSize: 11.5, color: tk.text3 }}>
            Investigation copilot · {chats.length} message{chats.length === 1 ? '' : 's'} · read only
          </Typography>
        </Box>
      </Box>

      <Box
        ref={scrollRef}
        sx={{ maxHeight: 620, overflow: 'auto', p: '18px', display: 'flex', flexDirection: 'column', gap: '13px' }}
      >
        {!chats.length && (
          <Box sx={{ maxWidth: '86%', p: '11px 14px', borderRadius: '14px', borderBottomLeftRadius: '4px', fontSize: 13.5, lineHeight: 1.5, background: tk.surface3, color: tk.text }}>
            No messages in chat yet
          </Box>
        )}
        {chats.map((c, i) => {
          const irbi = c.role === 'model'
          const when = c.dateTime ? formatDate(c.dateTime, 'dd/MM/yyyy HH:mm') : ''
          return (
            <Box
              key={i}
              sx={{
                maxWidth: '86%', p: '11px 14px', borderRadius: '14px', fontSize: 13.5, lineHeight: 1.5,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                ...(irbi
                  ? { background: tk.surface3, color: tk.text, borderBottomLeftRadius: '4px', alignSelf: 'flex-start' }
                  : { background: '#2D89F4', color: '#fff', alignSelf: 'flex-end', borderBottomRightRadius: '4px' }),
              }}
            >
              {irbi ? renderRichBody(c, tk) : c.text}
              {c.type === 'TRIAL_USER' && c.link && (
                <Box component="a" href={c.link} target="_blank" rel="noopener noreferrer" sx={{
                  display: 'inline-block', mt: '8px', fontSize: 12.5, fontWeight: 700,
                  color: '#fff', background: '#2D89F4', borderRadius: '999px', px: '13px', py: '7px', textDecoration: 'none',
                }}>
                  Packages
                </Box>
              )}
              {when && (
                <Box component="span" sx={{ display: 'block', mt: '4px', fontSize: 10.5, color: irbi ? tk.text3 : 'rgba(255,255,255,.75)' }}>
                  {when}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export function AIAssistantV2({ subscriber, dark }: { subscriber: Subscriber; dark?: boolean }) {
  const outer = useTheme()
  // result-v2's primitives take their colours from the MUI palette mode — follow the page's theme switch
  const muiTheme = React.useMemo(() => createTheme(outer, { palette: { mode: dark ? 'dark' : 'light' } }), [outer, dark])
  const raw = (subscriber as any)?.chatHistory?.data
  const chats: Chat[] = React.useMemo(() => (Array.isArray(raw) ? raw : []), [raw])
  return (
    <ThemeProvider theme={muiTheme}>
      <Thread chats={chats} />
    </ThemeProvider>
  )
}
